// SAD-866: a waiver or an extension is proposed first and waits for the COR.
// These are the COR's-answer fields (agreed: the COR's date, reason and an
// optional document; declined: the reason), shared by the date's own form,
// the propose forms' "The COR already agreed" shortcut and the batch drawer
// on Awaiting approval.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraCheckboxField, SpecraDateField, SpecraFormField, SpecraInlineBanner, SpecraSegmentedControl, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { FileUploader } from '../../../../components/ProjectList/FileUploader';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { useRecordCorAnswer, useUploadEvidenceFiles } from '../hooks';
import { CmDrawer } from './CmDrawer';
import { UseTodayButton } from './OccurrenceDrawer';
export const EMPTY_COR_ANSWER = {
    answer: 'agreed',
    on: '',
    reason: '',
    files: []
};
/** What the COR's answer still lacks, in words; null when it is complete. */ export function corAnswerProblem(v) {
    if (v.answer === 'agreed' && !v.on) return 'Give the date the COR agreed.';
    if (!v.reason.trim()) return v.answer === 'agreed' ? 'Give the COR’s reason.' : 'Say why the COR declined.';
    return null;
}
/**
 * The COR's date, reason and document. `fixed` hides the agreed/declined
 * choice (the propose forms' shortcut is always agreed).
 */ export function CorAnswerFields({ value, onChange, fixed, disabled, testIds = {} }) {
    const set = (patch)=>onChange({
            ...value,
            ...patch
        });
    const agreed = value.answer === 'agreed';
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        children: [
            fixed ? null : /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                label: "The COR’s answer",
                value: value.answer,
                onValueChange: (answer)=>set({
                        answer
                    }),
                options: [
                    {
                        value: 'agreed',
                        label: 'The COR agreed'
                    },
                    {
                        value: 'declined',
                        label: 'The COR declined'
                    }
                ],
                testId: testIds.answer
            }),
            agreed ? /*#__PURE__*/ _jsx(SpecraDateField, {
                picker: "native",
                label: "The COR agreed on",
                required: true,
                value: value.on,
                onValueChange: (on)=>set({
                        on
                    }),
                hint: /*#__PURE__*/ _jsx(UseTodayButton, {
                    onUse: (on)=>set({
                            on
                        })
                }),
                "data-testid": testIds.on
            }) : null,
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: agreed ? 'The COR’s reason' : 'Why the COR declined',
                required: true,
                hint: agreed ? 'A second person reads this before approving.' : 'The proposal closes; the date stays owed.',
                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                    rows: 2,
                    value: value.reason,
                    onChange: (e)=>set({
                            reason: e.target.value
                        }),
                    disabled: disabled,
                    "data-testid": testIds.reason
                })
            }),
            agreed ? /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "The COR’s documents",
                hint: "Optional: their emails (.eml) or letters. The approver can open them, and they stay on the date.",
                children: /*#__PURE__*/ _jsx(FileUploader, {
                    files: value.files,
                    onChange: (files)=>set({
                            files
                        }),
                    disabled: disabled,
                    inputTestid: testIds.evidence
                })
            }) : null
        ]
    });
}
/** "The COR already agreed": the propose forms' shortcut to the agreed step. */ export function CorAgreedShortcut({ agreed, onAgreed, value, onChange, disabled, onTestId, reasonTestId, testId = Testids.cmOccurrence.proposeCorAgreed }) {
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                label: "The COR already agreed",
                description: "Record their answer now; a second person still approves.",
                controlProps: {
                    checked: agreed,
                    disabled,
                    onCheckedChange: (v)=>onAgreed(v === true),
                    ...{
                        'data-testid': testId
                    }
                }
            }),
            agreed ? /*#__PURE__*/ _jsx(CorAnswerFields, {
                fixed: true,
                value: value,
                onChange: onChange,
                disabled: disabled,
                testIds: {
                    on: onTestId,
                    reason: reasonTestId,
                    evidence: Testids.cmOccurrence.corEvidence
                }
            }) : null
        ]
    });
}
/**
 * Records one COR answer on many proposals: the document, if any, is
 * uploaded once and named on each. One at a time, so a failure names its
 * date and the rest still run. A retry skips the proposals already recorded
 * and reuses the document already uploaded.
 */ export function useCorAnswerRunner(programId) {
    const record = useRecordCorAnswer(programId, {
        errors: 'inline'
    });
    const evidence = useUploadEvidenceFiles(programId);
    const [progress, setProgress] = useState(null);
    const recorded = useRef(new Set());
    const uploaded = useRef(null);
    const run = async (all, v)=>{
        const failed = [];
        const targets = all.filter((t)=>!recorded.current.has(t.changeSetId));
        let set = {
            documentId: '',
            attachments: []
        };
        const files = v.answer === 'agreed' ? v.files : [];
        if (files.length > 0) {
            try {
                let cached = uploaded.current;
                if (!cached || cached.files.length !== files.length || cached.files.some((f, i)=>f !== files[i])) {
                    cached = {
                        files,
                        set: await evidence.mutateAsync(files)
                    };
                    uploaded.current = cached;
                }
                set = cached.set;
            } catch (err) {
                return {
                    recorded: 0,
                    failures: [
                        apiErrorMessage(err, 'Could not upload the document')
                    ]
                };
            }
        }
        setProgress({
            done: 0,
            total: targets.length
        });
        for (const [i, t] of targets.entries()){
            try {
                await record.mutateAsync({
                    changeSetId: t.changeSetId,
                    agreed: v.answer === 'agreed',
                    corOn: v.answer === 'agreed' ? v.on : '',
                    reason: v.reason.trim(),
                    ...set.documentId ? {
                        documentId: set.documentId
                    } : {},
                    ...set.attachments.length > 0 ? {
                        attachments: set.attachments
                    } : {}
                });
                recorded.current.add(t.changeSetId);
            } catch (err) {
                failed.push(`${t.name}: ${apiErrorMessage(err, 'could not record')}`);
            }
            setProgress({
                done: i + 1,
                total: targets.length
            });
        }
        setProgress(null);
        return {
            recorded: targets.length - failed.length,
            failures: failed
        };
    };
    return {
        run,
        progress,
        busy: progress !== null || evidence.isPending
    };
}
/** The batch drawer on Awaiting approval: one COR answer for the picked proposals. */ export function CorAnswerDrawer({ programId, targets, onClose, onDone }) {
    const [value, setValue] = useState(EMPTY_COR_ANSWER);
    const [failures, setFailures] = useState([]);
    const runner = useCorAnswerRunner(programId);
    const n = targets.length;
    const problem = n === 0 ? 'Pick a proposal awaiting the COR.' : corAnswerProblem(value);
    const save = async ()=>{
        setFailures([]);
        const { recorded: ok, failures: failed } = await runner.run(targets.map((t)=>({
                changeSetId: t.decision.changeSetId,
                name: t.name
            })), value);
        if (ok > 0) toast.success(value.answer === 'agreed' ? `Recorded the COR’s agreement on ${ok}. A second person approves ${ok === 1 ? 'it' : 'them'}.` : `Recorded the COR declining ${ok}. ${ok === 1 ? 'The date stays' : 'The dates stay'} owed.`);
        if (failed.length > 0) setFailures(failed);
        else onDone();
    };
    return /*#__PURE__*/ _jsx(CmDrawer, {
        title: "Record the COR’s answer",
        description: `${n} ${n === 1 ? 'proposal' : 'proposals'} awaiting the COR. Nothing changes on a date until a second person approves it.`,
        onClose: onClose,
        busy: runner.busy,
        testId: Testids.cmApprovals.corDrawer,
        footerProps: {
            confirmLabel: `Record for ${n}`,
            onConfirm: ()=>void save(),
            confirmDisabled: !!problem || runner.busy,
            loading: runner.busy,
            onCancel: onClose,
            confirmProps: {
                'data-testid': Testids.cmApprovals.corSave
            },
            leading: runner.progress ? /*#__PURE__*/ _jsxs(SpecraText, {
                size: "caption",
                tone: "muted",
                children: [
                    "Recording ",
                    runner.progress.done,
                    " of ",
                    runner.progress.total,
                    "…"
                ]
            }) : problem ? /*#__PURE__*/ _jsx(SpecraText, {
                size: "caption",
                tone: "muted",
                children: problem
            }) : undefined
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx("ul", {
                    className: "space-y-0.5 text-[length:var(--fs-ui-body)]",
                    children: targets.map((t)=>/*#__PURE__*/ _jsx("li", {
                            children: t.name
                        }, t.decision.changeSetId))
                }),
                /*#__PURE__*/ _jsx(CorAnswerFields, {
                    value: value,
                    onChange: setValue,
                    disabled: runner.busy,
                    testIds: {
                        answer: Testids.cmOccurrence.corAnswer,
                        on: Testids.cmOccurrence.corOn,
                        reason: Testids.cmOccurrence.corReason,
                        evidence: Testids.cmOccurrence.corEvidence
                    }
                }),
                failures.length > 0 ? /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                    tone: "danger",
                    variant: "framed",
                    role: "alert",
                    children: [
                        failures.length === 1 ? 'One was' : `${failures.length} were`,
                        " not recorded:",
                        ' ',
                        failures.join('; ')
                    ]
                }) : null
            ]
        })
    });
}
